import { resources } from "@constants/hardcoded-strings";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingFormSection } from "@containers/common/theming-form-section/theming-form-section";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { ThemingPolicyRetryMessageStyleSection } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-policy-retry-message-tab-container/theming-policy-retry-message-style-section/theming-policy-retry-message-style-section";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { PolicyDecisionActionTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent, AXNUITheme } from "@iddataweb/idw-common-ui/types";
import { AXNUIContentPolicyDecisionRetryMessage } from "@iddataweb/idw-common-ui/types/axnui-content";
import { AXNUIThemePolicyRetryMessage } from "@iddataweb/idw-common-ui/types/axnui-theme/axnui-them-policy-retry-message";
import { ThemingPolicyRetryMessageValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { Fragment, useMemo, useState } from "react";

type ThemingPolicyRetryMessageTabContainerProps = {
    isDisabled: boolean;
    axnuiTheme: AXNUITheme;
    inheritedAXNUITheme: AXNUITheme;
    axnuiContent: AXNUIContent,
    pageLevel: PageLevelTypes;
    validationErrors: ThemingPolicyRetryMessageValidationErrors;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    inheritedAXNUIContent: AXNUIContent;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updateAXNUIThemePropertyValue: (axnuiTheme: AXNUITheme) => void;

};

export const ThemingPolicyRetryMessageTabContainer = (props: ThemingPolicyRetryMessageTabContainerProps) => {
    const {
        isDisabled,
        axnuiTheme,
        inheritedAXNUITheme,
        axnuiContent,
        pageLevel,
        inheritedAXNUIContent,
        validationErrors,
        rpServiceConfigurationState,
        generatePreviewComponent,
        updateAXNUIContentPropertyValue,
        updateAXNUIThemePropertyValue,
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const updateAXNUIContentPolicyDecisionRetryValue = (key: keyof AXNUIContentPolicyDecisionRetryMessage, value: string) => {
        const defaultContentPolicyRetryMessage = {
            retryMessage: value
        };
        const newPropertyValue: AXNUIContent = {
            ...axnuiContent,
            policyDecisionRetryMessage: {
                ...axnuiContent.policyDecisionRetryMessage,
                [key]: {
                    ...defaultContentPolicyRetryMessage
                }
            }
        };
        updateAXNUIContentPropertyValue(newPropertyValue);
    };

    const updateAXNUIThemePolicyRetryPropertyValue = (key: keyof AXNUIThemePolicyRetryMessage, value: string) => {
        const newPropertyValue: AXNUITheme = {
            ...axnuiTheme,
            policyRetryMessage: {
                ...axnuiTheme.policyRetryMessage,
                [key]: value
            }
        };
        updateAXNUIThemePropertyValue(newPropertyValue);
    };
    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.POLICY_RETRY_MESSAGE);
    }, [generatePreviewComponent]);

    const handleErrorMessageChange = (key: keyof AXNUIContentPolicyDecisionRetryMessage, event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        updateAXNUIContentPolicyDecisionRetryValue(key, event.target.value);
    };
    const orderedKeys = (() => {
        const getProperOrder: { key: string, retryMessage: string }[] = [];

        rpServiceConfigurationState.policyConfig.decisions.forEach(decision => {
            Object.keys(axnuiContent.policyDecisionRetryMessage).forEach(key => {
                if (key === decision.uuid || key === decision.id) {
                    getProperOrder.push({ key, retryMessage: axnuiContent.policyDecisionRetryMessage[key].retryMessage });
                }
            });
        });
        getProperOrder.push({ key: 'default', retryMessage: axnuiContent.policyDecisionRetryMessage['default'].retryMessage });

        return getProperOrder;
    })();

    const handleResetErrorMessage = (key: keyof AXNUIContentPolicyDecisionRetryMessage) => {
        if (key === 'default') {
            updateAXNUIContentPolicyDecisionRetryValue(key, inheritedAXNUIContent.policyDecisionRetryMessage.default.retryMessage)
        } else {
            updateAXNUIContentPolicyDecisionRetryValue(key, axnuiContent.policyDecisionRetryMessage['default'].retryMessage)
        }

    };

    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.title}></ContentPageHeader>
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection>
                            {orderedKeys.map(({ key, retryMessage }, index) => {
                                rpServiceConfigurationState.policyConfig.decisions.forEach(decision => {
                                    if (decision.policyDecisionType == PolicyDecisionActionTypes.DENY) {
                                        if (key == decision.uuid || key == decision.id) {
                                            return null;
                                        }
                                    }
                                });
                                const label = key === 'default'
                                    ? resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.policyRetryMessage.defaultLabel
                                    : `Custom Rule #${index + 1} ${resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.policyRetryMessage.label}`;
                                return (
                                    <Fragment key={key}>
                                        <TextareaFormGroup
                                            controlId={`theming-retry-policy-message-input-${key}`}
                                            automationId={`theming-retry-policy-message-input-${key}`}
                                            label={label}
                                            ariaLabel={label}
                                            layout={layout}
                                            labelWidth={labelWidth}
                                            onChange={(event) => handleErrorMessageChange(key, event)}
                                            value={retryMessage || ""}
                                            disabled={isDisabled}
                                            isRequired={false}
                                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.policyRetryMessage.inputs.policyRetryMessage.tooltip}
                                            afterLabelIcon={isDisabled ? null : faUndo}
                                            afterLabelIconOnClick={() => handleResetErrorMessage(key)}
                                        />
                                        {validationErrors.policyConfigDecisionsRetryErrors[key] || null}
                                    </Fragment>
                                );
                            })}
                        </ThemingFormSection>
                        <ThemingFormSection title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.sections.style.title} showSeparator={true}>
                            <ThemingPolicyRetryMessageStyleSection
                                labelWidth={labelWidth}
                                layout={layout}
                                isDisabled={isDisabled}
                                axnuiTheme={axnuiTheme}
                                inheritedAXNUITheme={inheritedAXNUITheme}
                                validationErrors={validationErrors}
                                pageLevel={pageLevel}
                                updateAXNUIThemePolicyRetryPropertyValue={updateAXNUIThemePolicyRetryPropertyValue}
                            />
                        </ThemingFormSection>
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};